import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Search, UserRound } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useManagesEndUsers } from '../customers/AddCustomer';
import { endUserStatus } from '../customers/CustomerUsers';
import { listCustomerUsers } from '../lib/api';
import { recordPath } from '../records/paths';
import { useScope } from '../scope/scope';
import { useCatalog } from '../search/hooks';
import { ErrorPanel } from '../ui/errors';
import { Card, EmptyState, Skeleton } from '../ui/misc';
import { PageHeader } from './PageHeader';

/**
 * Every customer's end users in one list (WRMM-220; Harry: "i should see them in the list of end users ... i should be
 * able to click into a user and adjust the devices they can see"). Owners, and technicians who manage end users for
 * some customers (WRMM-374): the API lists only theirs. A row opens that
 * person on their customer's page, with their computers ready to change for an owner.
 */
export function EndUsersPage() {
  const { mspId } = useScope();
  const manages = useManagesEndUsers();
  const { catalog } = useCatalog();
  const [text, setText] = useState('');
  const users = useQuery({ queryKey: ['scope', mspId, 'customer-users', 'all'], enabled: manages, queryFn: ({ signal }) => listCustomerUsers(mspId, undefined, signal) });
  const customers = new Map(catalog.organizations.map(o => [o.id, o.title]));
  const rows = (users.data ?? []).map(u => ({ ...u, customer: customers.get(u.organizationId) ?? 'Customer' }))
    .sort((a, b) => a.customer.localeCompare(b.customer) || (a.name ?? a.email ?? '').localeCompare(b.name ?? b.email ?? ''));
  const wanted = text.trim().toLowerCase();
  const shown = rows.filter(r => !wanted || [r.name, r.email, r.customer].some(v => v?.toLowerCase().includes(wanted)));
  if (!manages) return (
    <div className="space-y-5">
      <PageHeader title="End users" />
      <Card><EmptyState icon={<UserRound className="size-5" />} title="You don’t manage end users">Ask an owner to let you manage end users for your customers.</EmptyState></Card>
    </div>
  );
  return (
    <div className="space-y-5">
      <PageHeader title="End users" description={users.data ? `${rows.length} end user${rows.length === 1 ? '' : 's'} across your customers · add them on a customer’s page` : 'Loading…'} />
      <label className="relative flex max-w-md items-center">
        <Search className="pointer-events-none absolute left-3 size-4 text-ink-3" />
        <input data-page-search value={text} onChange={e => setText(e.target.value)} placeholder="Find an end user or customer" aria-label="Find an end user or customer"
          className="h-10 w-full rounded-xl border border-line bg-panel pl-9 pr-3 shadow-card outline-none focus:border-brand/50 focus:ring-4 focus:ring-brand/10" />
      </label>
      {users.isError ? <Card><ErrorPanel error={users.error} onRetry={() => void users.refetch()} /></Card>
        : users.isPending ? <Card className="space-y-3 p-5"><Skeleton className="h-5 w-1/2" /><Skeleton className="h-5 w-2/3" /></Card>
        : !shown.length ? <Card><EmptyState icon={<UserRound className="size-5" />} title={rows.length ? 'No end users match' : 'No end users yet'}>
            {rows.length ? 'Try a different name.' : 'Add the people at a customer from that customer’s page.'}</EmptyState></Card>
        : (
          <Card className="p-0">
            <ul className="divide-y divide-line" aria-label="End users">
              {shown.map(u => (
                <li key={u.id}>
                  <Link to={`${recordPath(mspId, 'organization', u.organizationId)}?person=${encodeURIComponent(u.id)}`}
                    className="group flex items-center gap-3 px-5 py-3 transition-colors hover:bg-hover">
                    <UserRound className="size-4 shrink-0 text-ink-3" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium group-hover:underline">{u.name ?? u.email}{!u.active && <span className="ml-2 text-[12px] text-attention">turned off</span>}</p>
                      <p className="truncate text-[12.5px] text-ink-3">{u.email} · {u.customer} · {endUserStatus(u)} · {u.devices.length
                        ? `${u.devices.length} computer${u.devices.length === 1 ? '' : 's'}` : 'no computers'}</p>
                    </div>
                    <ChevronRight className="size-4 text-ink-3" />
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        )}
    </div>
  );
}
